import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Avatar,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  CircularProgress,
} from "@mui/material";
import { Check } from "@mui/icons-material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  useGetProjectForDropDownQuery,
  useGetDefaultStatusQuery,
  useGetListOfStatusQuery,
} from "@/features/bughurd/api";
import IOSSwitch from "../IOSSwitch";
import { useJiraFields } from "../hooks/useJiraFields";
import type { IntegrationState } from "../types";

interface JiraConfigProps {
  projectDetails: any;
  state: IntegrationState;
  isJiraConfigured: boolean;
  handleProjectChange: (event: any, newValue: any) => void;
  updateFieldConfig: (
    key: string,
    fieldKey: string,
    value?: string | boolean,
    dataType?: string,
  ) => void;
  handleDefaultFieldChange: (
    key: string,
    fieldName?: string,
    fieldKey?: string,
    value?: string | boolean,
    dataType?: string,
    allowedValues?: any,
  ) => void;
  onNavigateToCompanySettings: () => void;
}

export default function JiraConfig({
  projectDetails,
  state,
  isJiraConfigured,
  handleProjectChange,
  updateFieldConfig,
  handleDefaultFieldChange,
  onNavigateToCompanySettings,
}: JiraConfigProps) {
  const { id } = useParams();
  const { t } = useTranslation(["bugherd"]);
  const [tabValue, setTabValue] = useState(1);

  // Fetch JIRA projects
  const { data: jiraProjectsData, isLoading: isLoadingProjects } =
    useGetProjectForDropDownQuery({});
  const jiraProjects = jiraProjectsData?.data?.decryptedData?.data || [];

  // Fetch JIRA fields and status labels
  const { combinedResponse, combinedStatus, loading } = useJiraFields(
    state.jiraConfig,
    jiraProjects,
  );

  // Fetch status lists for mapping
  const { data: defaultStatus } = useGetDefaultStatusQuery({});
  const { data: statusList } = useGetListOfStatusQuery(
    { projectId: id },
    { skip: !id },
  );

  const handleTabChange = (_: any, newValue: any) => {
    setTabValue(newValue);
  };

  // Switching integration warning
  const existingIntegration = projectDetails?.integrationType;
  const showProjectWarning =
    existingIntegration &&
    existingIntegration !== "NONE" &&
    existingIntegration !== "JIRA";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2.5,
        borderRadius: "10px",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: (theme) => theme.palette.background.paper,
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight={600}
        fontSize="13px"
        mb={2}
        color="text.secondary"
      >
        {t("Jira Integration")}
      </Typography>

      {/* Warning when switching Project Management integration */}
      {showProjectWarning && (() => {
        const existingName =
          existingIntegration === "JIRA"
            ? "Jira"
            : existingIntegration === "AZURE"
              ? "Azure Boards"
              : "Asana";

        return (
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: "8px",
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 152, 0, 0.1)"
                  : "#fff3e0",
              border: (theme) =>
                `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 152, 0, 0.3)" : "#ffcc80"}`,
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <WarningAmberIcon
                    sx={{ color: "#f57c00", fontSize: 18 }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    fontSize="13px"
                    color="#e65100"
                  >
                    {t("Switching Project Management Integration")}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  fontSize="12px"
                  color="text.secondary"
                  lineHeight={1.5}
                >
                  {t("switchingIntegrationWarning", {
                    existing: existingName,
                    new: "Jira",
                  })}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })()}

      {/* Jira Project Selection */}
      <Autocomplete
        options={jiraProjects?.length > 0 ? jiraProjects : []}
        getOptionLabel={(option: any) => option?.name || ""}
        loading={isLoadingProjects}
        value={
          state.jiraConfig?.length > 0 && jiraProjects?.length > 0
            ? jiraProjects?.find(
                (p: any) => p?.id === state.jiraConfig[0]?.projectId,
              ) || null
            : null
        }
        onChange={handleProjectChange}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Avatar
                src={option?.avatarUrls?.["48x48"]}
                sx={{ width: 24, height: 24, mr: 1 }}
              />
              <Typography sx={{ flexGrow: 1 }}>
                {option?.name} ({option?.key})
              </Typography>
              {selected && (
                <Check
                  sx={{
                    color: (theme) => theme.palette.primary.main,
                    ml: 1,
                  }}
                />
              )}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("Select Jira Project")}
            placeholder={t("Select Jira Project")}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {state.jiraConfig?.length > 0 &&
                    jiraProjects?.length > 0 &&
                    jiraProjects?.find(
                      (p: any) =>
                        p?.id === state.jiraConfig[0]?.projectId,
                    ) && (
                      <Avatar
                        src={
                          jiraProjects?.find(
                            (p: any) =>
                              p?.id === state.jiraConfig[0]?.projectId,
                          )?.avatarUrls?.["48x48"]
                        }
                        sx={{
                          width: 24,
                          height: 24,
                          mr: 1,
                          ml: 0.5,
                        }}
                      />
                    )}
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        disabled={!isJiraConfigured}
      />

      {/* Not configured warning */}
      {!isJiraConfigured && (
        <Box
          sx={{
            mt: 1,
            mb: 1,
            p: 1.5,
            borderRadius: "6px",
            bgcolor: (theme) => `${theme.palette.error.main}08`,
            border: (theme) =>
              `1px solid ${theme.palette.error.main}30`,
          }}
        >
          <Typography
            variant="caption"
            color="error"
            sx={{ fontSize: "11.5px" }}
          >
            {t(
              "JIRA integration is not configured or does not exist any project. Click",
            )}{" "}
            <span
              onClick={onNavigateToCompanySettings}
              style={{
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: 600,
              }}
            >
              {t("here")}
            </span>{" "}
            {t("to integrate.")}
          </Typography>
        </Box>
      )}

      {/* JIRA Configuration Tabs */}
      {state.jiraConfig?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <TabContext value={tabValue.toString()}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 36,
                  "& .MuiTab-root": {
                    minHeight: 36,
                    fontSize: "12.5px",
                    textTransform: "none",
                    fontWeight: 500,
                    py: 0.5,
                  },
                }}
              >
                {state.jiraConfig?.map((config, index) => (
                  <Tab
                    key={config.key}
                    label={config.projectData?.name || config.key}
                    value={(index + 1).toString()}
                  />
                ))}
              </TabList>
            </Box>

            {state.jiraConfig?.map((config, index) => {
              const currentProject = combinedResponse?.find(
                (item) => item.key === config.key,
              );
              const currentStatus = combinedStatus?.find(
                (item) => item.key === config.key,
              );

              return (
                <TabPanel
                  key={config.key}
                  value={(index + 1).toString()}
                  sx={{ px: 0.5, py: 1.5 }}
                >
                  {/* Sync Configuration Section */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: "8px",
                      border: (theme) =>
                        `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      fontSize="12.5px"
                      mb={1.5}
                      color="text.secondary"
                    >
                      {t("Sync Configuration")}
                    </Typography>

                    <Stack spacing={1.5}>
                      {/* Sync Tasks to JIRA */}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography variant="body2">
                            {t("Automatically create task in JIRA")}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {t(
                              "When a task is created, it will be automatically synced to JIRA",
                            )}
                          </Typography>
                        </Box>
                        <IOSSwitch
                          checked={
                            config.fieldConfig?.isAutomaticCreateJira ||
                            false
                          }
                          onChange={(e: any) =>
                            updateFieldConfig(
                              config.key,
                              "isAutomaticCreateJira",
                              e.target.checked,
                              "boolean",
                            )
                          }
                        />
                      </Box>

                      {/* Sync Comments to JIRA */}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography variant="body2">
                            {t("Sync comments to JIRA")}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {t(
                              "Comments added here will be synced to JIRA",
                            )}
                          </Typography>
                        </Box>
                        <IOSSwitch
                          checked={
                            config.fieldConfig?.isCreateComment || false
                          }
                          onChange={(e: any) =>
                            updateFieldConfig(
                              config.key,
                              "isCreateComment",
                              e.target.checked,
                              "boolean",
                            )
                          }
                        />
                      </Box>

                      {/* Sync Comments from JIRA */}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography variant="body2">
                            {t("Sync comments from JIRA")}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {t(
                              "Comments from JIRA will be synced here",
                            )}
                          </Typography>
                        </Box>
                        <IOSSwitch
                          checked={
                            config.fieldConfig?.isSyncComment || false
                          }
                          onChange={(e: any) =>
                            updateFieldConfig(
                              config.key,
                              "isSyncComment",
                              e.target.checked,
                              "boolean",
                            )
                          }
                        />
                      </Box>

                      {/* Sync from JIRA */}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography variant="body2">
                            {t("Sync tasks from JIRA")}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {t(
                              "Tasks created in JIRA will be synced here",
                            )}
                          </Typography>
                        </Box>
                        <IOSSwitch
                          checked={
                            config.fieldConfig?.syncFromJira || false
                          }
                          onChange={(e: any) =>
                            updateFieldConfig(
                              config.key,
                              "syncFromJira",
                              e.target.checked,
                              "boolean",
                            )
                          }
                        />
                      </Box>
                    </Stack>
                  </Paper>

                  {/* Status Mapping Section */}
                  {currentStatus?.status?.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: "8px",
                        border: (theme) =>
                          `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        fontSize="12.5px"
                        mb={1.5}
                        color="text.secondary"
                      >
                        {t("Status Mapping")}
                      </Typography>

                      <Box display="flex" gap={2}>
                        <FormControl fullWidth>
                          <InputLabel>
                            {t("Completed Status (JIRA)")}
                          </InputLabel>
                          <Select
                            value={
                              config.fieldConfig?.completedStatus || ""
                            }
                            onChange={(e) =>
                              updateFieldConfig(
                                config.key,
                                "completedStatus",
                                e.target.value,
                              )
                            }
                            label={t("Completed Status (JIRA)")}
                          >
                            {currentStatus.status.map((status: any) => (
                              <MenuItem
                                key={status.id}
                                value={status.id}
                              >
                                {status.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth>
                          <InputLabel>
                            {t("Completed Column (Local)")}
                          </InputLabel>
                          <Select
                            value={
                              config.fieldConfig?.completedColumn || ""
                            }
                            onChange={(e) =>
                              updateFieldConfig(
                                config.key,
                                "completedColumn",
                                e.target.value,
                              )
                            }
                            label={t("Completed Column (Local)")}
                          >
                            {statusList?.data?.decryptedData?.items?.map(
                              (status: any) => (
                                <MenuItem
                                  key={status._id}
                                  value={status._id}
                                >
                                  {status.statusName}
                                </MenuItem>
                              ),
                            ) ||
                              defaultStatus?.data?.decryptedData?.items?.map(
                                (status: any) => (
                                  <MenuItem
                                    key={status._id}
                                    value={status._id}
                                  >
                                    {status.statusName}
                                  </MenuItem>
                                ),
                              )}
                          </Select>
                        </FormControl>
                      </Box>
                    </Paper>
                  )}

                  {/* Required Fields Section */}
                  {currentProject?.data?.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: "8px",
                        border: (theme) =>
                          `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        fontSize="12.5px"
                        mb={1.5}
                        color="text.secondary"
                      >
                        {t("Required Fields Configuration")}
                      </Typography>

                      <TableContainer
                        sx={{
                          borderRadius: "6px",
                          border: (theme) =>
                            `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <strong>{t("Field Name")}</strong>
                              </TableCell>
                              <TableCell>
                                <strong>{t("Default Value")}</strong>
                              </TableCell>
                              <TableCell align="center">
                                <strong>{t("Auto-fill")}</strong>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentProject.data.map((field: any) => (
                              <TableRow key={field.fieldKey}>
                                <TableCell>
                                  {field.fieldName}
                                </TableCell>
                                <TableCell>
                                  {field.dataType === "array" ? (
                                    <Select
                                      size="small"
                                      fullWidth
                                      value={
                                        config.defaultFields?.[
                                          field.fieldName
                                        ]?.[field.fieldKey] || ""
                                      }
                                      onChange={(e) =>
                                        handleDefaultFieldChange(
                                          config.key,
                                          field.fieldName,
                                          field.fieldKey,
                                          e.target.value,
                                          field.dataType,
                                          field.allowedValues,
                                        )
                                      }
                                    >
                                      {field.allowedValues?.map(
                                        (val: any) => (
                                          <MenuItem
                                            key={val.id}
                                            value={val.id}
                                          >
                                            {val.name || val.value}
                                          </MenuItem>
                                        ),
                                      )}
                                    </Select>
                                  ) : (
                                    <TextField
                                      size="small"
                                      fullWidth
                                      type={
                                        field.dataType === "number"
                                          ? "number"
                                          : "text"
                                      }
                                      value={
                                        config.defaultFields?.[
                                          field.fieldName
                                        ]?.[field.fieldKey] || ""
                                      }
                                      onChange={(e) =>
                                        handleDefaultFieldChange(
                                          config.key,
                                          field.fieldName,
                                          field.fieldKey,
                                          e.target.value,
                                          field.dataType,
                                        )
                                      }
                                    />
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <Switch
                                    checked={
                                      config.defaultFields?.[
                                        field.fieldName
                                      ]?.method || false
                                    }
                                    onChange={(e) =>
                                      handleDefaultFieldChange(
                                        config.key,
                                        field.fieldName,
                                        field.fieldKey,
                                        e.target.checked,
                                        field.dataType,
                                        field.allowedValues,
                                      )
                                    }
                                    size="small"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  )}

                  {loading && (
                    <Box display="flex" justifyContent="center" mt={2}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </TabPanel>
              );
            })}
          </TabContext>
        </Box>
      )}
    </Paper>
  );
}
